import { useContext, useEffect, useState } from 'react';
import { useHTTP } from 'hooks/useRequest';
import Button from 'components/ui-components/Button';
import { PostcodeSelectorContext } from '../PostcodeSelectorProvider';
import {
  setIsLoading,
  initiateSearch,
  setFetchedAddresses,
  manualEntry,
  forceManualEntry,
  resetAddressFields,
} from '../postcodeSelectorStateManager';
import {
  formatAddressArray,
  resetFieldValidationAndErrors,
} from '../postcodeSelectorUtils';
import { postcodeDoesNotExist, postcodeApiFail } from '../errorMessages';

const PostcodeActionButtons = () => {
  const {
    id,
    dispatch,
    postcode,
    setPostcodeSearched,
    showManualEntryButton,
    errors,
    setError,
    clearErrors,
    setValue,
  } = useContext(PostcodeSelectorContext);

  const [fetchPostcode, setFetchPostcode] = useState(false);

  const requestConfig = {
    url: fetchPostcode ? `/address_lookup?postcode=${postcode}` : null,
    method: 'get',
    headers: {
      Accept: 'application/json',
    },
  };
  const { error, data, loading } = useHTTP(requestConfig);

  useEffect(() => {
    dispatch(setIsLoading(loading));
  }, [loading]);

  useEffect(() => {
    if (error && error.error) {
      const { status } = error.error;

      // Postcode format is valid but postcode doesn't exist
      if (status === 400) {
        setError(`${id}SearchPostcode`, {
          type: 'fatal',
          message: postcodeDoesNotExist,
        });
      }

      // API error
      if (status === 404 || status === 406) {
        setError(`${id}SearchPostcode`, {
          type: 'fatal',
          message: postcodeApiFail,
        });
      }

      dispatch(forceManualEntry()); // Hide Manual Entry button and show Manual Entry fields
      setFetchPostcode(false); // Stop API call
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const transformedData = formatAddressArray(data); // Removes underscores from keys
      dispatch(setFetchedAddresses(transformedData));
      setFetchPostcode(false); // Stop API call
    }
  }, [data]);

  // Reset field helper
  const resetField = resetFieldValidationAndErrors(setValue, clearErrors);

  const resetPostcodeSearchField = () => {
    setPostcodeSearched(true);
    resetField(`${id}SearchPostcode`, `${id}SearchPostcodeValid`);
  };

  const resetAddressFieldsValidation = () => {
    resetField(`${id}AddressSelector`, `${id}AddressSelectorValid`); // Not needed for validation
    resetField(`${id}Line1`, `${id}Line1Valid`, false);
    resetField(`${id}Postcode`, `${id}PostcodeValid`, false);
  };

  const handleSearchClick = () => {
    dispatch(initiateSearch());
    resetPostcodeSearchField();
    setFetchPostcode(true); // Start API call
  };

  const handleManualEntryClick = () => {
    dispatch(manualEntry());

    // Reset validation on postcode search field
    resetPostcodeSearchField();

    // Reset validation on required address fields
    resetAddressFieldsValidation();

    // Reset address field values
    dispatch(resetAddressFields());
  };

  return (
    <div className="mt-10 fl fl-col m-fl-row g-8 l-g-14">
      <Button
        className="cef-btn cef-btn--small-feature cef-btn--grey m-width-40 ws-nw"
        handleClick={() => handleSearchClick()}
        disabled={
          (errors?.[`${id}SearchPostcode`] &&
            errors?.[`${id}SearchPostcode`].type !== 'notTriggered') ||
          postcode === '' ||
          loading
        }>
        Find Address
      </Button>

      {showManualEntryButton && (
        <Button
          className="cef-btn cef-btn--small-feature cef-btn--grey-outlined ws-nw"
          handleClick={() => handleManualEntryClick()}>
          Enter Address Manually
        </Button>
      )}
    </div>
  );
};

export default PostcodeActionButtons;
